import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { LogOut, Settings } from "lucide-react";
import { useAuthStore } from "@/shared/stores/authStore";
import styles from "./ProfileMenuButton.module.css";

export type ProfileMenuPlacement = "bottom-end" | "top-start";

export interface ProfileMenuButtonProps {
  onNavigate: (path: string) => void;
  placement?: ProfileMenuPlacement;
  /** When set, used as the trigger button base (e.g. rail `.railButton`). */
  triggerClassName?: string;
}

function initialsFor(name: string | null | undefined, email: string | null | undefined) {
  const source = name?.trim() || email?.split("@")[0]?.replace(/[._-]+/g, " ") || "User";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return (parts[0]?.slice(0, 2) || "U").toUpperCase();
}

interface AvatarProps {
  user: { name?: string | null; email?: string | null; avatarUrl?: string | null };
  initials: string;
  size: "sm" | "md" | "rail";
  className?: string;
}

function Avatar({ user, initials, size, className }: AvatarProps) {
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setErrored(false);
  }, [user.avatarUrl]);

  const sizeClass =
    size === "sm" ? styles.avatarSm : size === "md" ? styles.avatarMd : styles.avatarRail;
  const cls = [sizeClass, className].filter(Boolean).join(" ");

  if (user.avatarUrl && !errored) {
    return (
      <img
        src={user.avatarUrl}
        alt={`${user.name || user.email || "User"} avatar`}
        className={`${styles.avatarImage} ${cls}`}
        onError={() => setErrored(true)}
        referrerPolicy="no-referrer"
      />
    );
  }

  return <span className={`${styles.avatarFallback} ${cls}`}>{initials}</span>;
}

export function ProfileMenuButton({
  onNavigate,
  placement = "bottom-end",
  triggerClassName,
}: ProfileMenuButtonProps) {
  const { user, signOut } = useAuthStore();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<
    | { top: number; right: number; placement: "bottom-end" }
    | { top: number; left: number; placement: "top-start" }
    | null
  >(null);

  const isRail = Boolean(triggerClassName);

  useLayoutEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (placement === "bottom-end") {
        setCoords({
          placement: "bottom-end",
          top: rect.bottom + 8,
          right: Math.max(8, window.innerWidth - rect.right),
        });
        return;
      }

      const menuEl = menuRef.current;
      const menuHeight = menuEl?.offsetHeight ?? 200;
      const menuWidth = menuEl?.offsetWidth ?? 240;

      let top = rect.top - menuHeight - 8;
      top = Math.max(8, Math.min(top, window.innerHeight - menuHeight - 8));
      let left = rect.right + 8;
      left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));

      setCoords({ placement: "top-start", top, left });
    };

    updatePosition();
    const raf = window.requestAnimationFrame(() => {
      updatePosition();
    });

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    // Escape closes only the menu. The handled event tells the app not to
    // close the phone drawer that holds the rail too.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const initials = useMemo(
    () => initialsFor(user?.name, user?.email),
    [user?.name, user?.email],
  );

  const handleSettings = useCallback(() => {
    setOpen(false);
    onNavigate("/settings");
  }, [onNavigate]);

  const handleLogout = useCallback(() => {
    setOpen(false);
    void signOut();
  }, [signOut]);

  if (!user) return null;

  const triggerBase = triggerClassName
    ? open
      ? styles.triggerRailOpen
      : styles.triggerRail
    : open
      ? styles.triggerHeaderOpen
      : styles.triggerHeader;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={[triggerClassName, triggerBase].filter(Boolean).join(" ")}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open profile menu"
        title={user.name || user.email || "Profile"}
      >
        <Avatar user={user} initials={initials} size={isRail ? "rail" : "sm"} />
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              className={styles.menu}
              role="menu"
              style={
                coords.placement === "bottom-end"
                  ? { top: coords.top, right: coords.right }
                  : { top: coords.top, left: coords.left }
              }
            >
              <div className={styles.identity}>
                <Avatar
                  user={user}
                  initials={initials}
                  size="md"
                  className={styles.identityAvatar}
                />
                <div className={styles.identityText}>
                  <div className={styles.identityName}>{user.name || "User"}</div>
                  <div className={styles.identityEmail}>{user.email}</div>
                </div>
              </div>
              <div className={styles.menuList}>
                <button
                  type="button"
                  className={styles.menuItem}
                  role="menuitem"
                  onClick={handleSettings}
                >
                  <Settings className={styles.menuIcon} />
                  <span>Settings</span>
                </button>
                <button
                  type="button"
                  className={styles.menuItem}
                  role="menuitem"
                  onClick={handleLogout}
                >
                  <LogOut className={styles.menuIcon} />
                  <span>Log out</span>
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
